import React, { useContext, useRef, useMemo, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
/**
 * Atoms
 */
import { Listbox } from '../../lab/Listbox';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
/**
 * Hooks
 */
import { useIsMobileView } from '@front10/utils';

const FlightTypeFilter = (props, ref) => {
  const {
    value,
    labelKey = 'cmp-location-selector-label-flight-type',
    options = [
      { label: 'cmp-journey-type-options-ow', value: 'ONE_WAY' },
      { label: 'cmp-journey-type-options-rt', value: 'ROUND_TRIP' },
      { label: 'cmp-journey-type-options-mc', href: 'https://google.com' }
    ],
    disabled,
    mobileView = false,
    onChange,
    filterButtonIconProps
  } = props;

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const title = translate('cmp-flight-type-title', 'Flight Type');
  const valueTranslated = useMemo(() => {
    return options
      .filter((option) => option.value === value)
      .map((option) => {
        return translate(option.label);
      })
      .pop();
  }, [options, translate, value]);

  const flightTypeOptions = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((option) => ({
      ...option,
      label: translate(option.label)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translate]);

  const flightTypeRef = useRef(null);
  const [flightTypeFilter, setFlightType] = useState(value);

  React.useEffect(() => {
    setFlightType(value);
  }, [value]);

  let isMobile = useIsMobileView();
  if (mobileView) {
    isMobile = true;
  }

  const listboxRef = useRef(null);

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        listboxRef.current.focusToggleElement();
      }
    }),
    []
  );

  const desktopLayout = (
    <Listbox
      ref={listboxRef}
      label={translate(labelKey, 'Select Flight Type')}
      placeholder={title}
      options={flightTypeOptions}
      defaultValue={value}
      forcePlaceholder={disabled}
      focusWhenIsShow={false}
      disabled={disabled}
      onChange={(value) => onChange({ value })}
      filterButtonIconProps={filterButtonIconProps}
    />
  );

  const mobileLayout = (
    <FlexibleFilterContainer
      showSeparator
      saveOnClose={false}
      variant="layout.popover"
      label={title}
      title={title}
      disabledDoneButton={false}
      enabledDoneButton
      labelBottomBtn={translate('cmp-flight-type-done-button', 'Done')}
      toggleElement={
        <FilterButton
          iconSettings={filterButtonIconProps}
          value={value && !disabled ? `${valueTranslated} ` : title}
          aria-label={`${title} ${value ? valueTranslated : ''}`}
          active={Boolean(value)}
          disabled={disabled} // used to disabled the component
        />
      }
      onSave={() => {
        props.onChange({ value: flightTypeFilter });
      }}
      onDismiss={() => {
        setFlightType(value);
      }}
    >
      <Panels showSeparator={false}>
        {options.length >= 1 && (
          <Listbox
            ref={flightTypeRef}
            label={translate('cmp-flight-type-placeholder', 'Select Flight Type')}
            layout="standard"
            options={flightTypeOptions}
            key={Math.floor(Math.random() * 100 + 1)} // ???
            defaultValue={flightTypeFilter}
            onChange={(newFlightType) => {
              setFlightType(newFlightType);
            }}
          />
        )}
      </Panels>
    </FlexibleFilterContainer>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>{!isMobile ? desktopLayout : mobileLayout}</ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(FlightTypeFilter);
